<template>
  <DataCard
    title="Description magasin"
    icon="fas fa-globe"
    icon-color="text-blue-600"
    icon-bg-color="bg-blue-100"
    class="w-full"
  >
    <template #header-actions>
      <div class="flex flex-wrap justify-end gap-2">
        <button
          type="button"
          @click="translateFromFrench"
          :disabled="saving || translating || !formData.description.fr.trim()"
          class="px-4 py-2 border border-blue-600 text-blue-700 bg-white rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <i :class="translating ? 'fas fa-spinner fa-spin' : 'fas fa-language'"></i>
          <span>{{ translating ? 'Traduction...' : 'Traduire le français' }}</span>
        </button>
        <button
          type="button"
          @click="$emit('save')"
          :disabled="saving || translating || disableSave"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <i v-if="saving" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-save"></i>
          <span>{{ saving ? 'Sauvegarde...' : 'Sauvegarder' }}</span>
        </button>
      </div>
    </template>
    <p v-if="translationMessage" class="mb-4 text-sm" :class="translationError ? 'text-red-700' : 'text-green-700'">
      {{ translationMessage }}
    </p>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Français -->
      <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <img src="@/assets/images/france.png" alt="Français" class="w-5 h-3" />
          </div>
          <h3 class="text-lg font-semibold text-black">Français</h3>
        </div>
                  <textarea
            :value="formData.description.fr"
            @input="updateFormData('description.fr', ($event.target as HTMLTextAreaElement).value)"
            rows="8"
            placeholder="Décrivez votre entreprise en français..."
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 resize-none transition-colors"
          ></textarea>
      </div>

      <!-- English -->
      <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <img src="@/assets/images/royaume-uni.png" alt="English" class="w-5 h-3" />
          </div>
          <h3 class="text-lg font-semibold text-black">English</h3>
        </div>
                  <textarea
            :value="formData.description.en"
            @input="updateFormData('description.en', ($event.target as HTMLTextAreaElement).value)"
            rows="8"
            placeholder="Describe your company in English..."
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 resize-none transition-colors"
          ></textarea>
      </div>

      <!-- Español -->
      <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <img src="@/assets/images/espagne.png" alt="Español" class="w-5 h-3" />
          </div>
          <h3 class="text-lg font-semibold text-black">Español</h3>
        </div>
                  <textarea
            :value="formData.description.es"
            @input="updateFormData('description.es', ($event.target as HTMLTextAreaElement).value)"
            rows="8"
            placeholder="Describe su empresa en español..."
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 resize-none transition-colors"
          ></textarea>
      </div>

      <!-- Italiano -->
      <div class="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
        <div class="flex items-center gap-3 mb-4">
          <div class="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <img src="@/assets/images/italie.png" alt="Italiano" class="w-5 h-3" />
          </div>
          <h3 class="text-lg font-semibold text-black">Italiano</h3>
        </div>
                  <textarea
            :value="formData.description.it"
            @input="updateFormData('description.it', ($event.target as HTMLTextAreaElement).value)"
            rows="8"
            placeholder="Descrivi la tua azienda in italiano..."
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 resize-none transition-colors"
          ></textarea>
      </div>
    </div>
  </DataCard>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { DataCard } from '@/components'
import { translateVendeurDescriptionFromFrench } from '@/api/vendeur'
import type { GeneralInformationFormData } from '@/types'

const props = defineProps<{
  formData: GeneralInformationFormData
  saving?: boolean
  disableSave?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:formData', value: GeneralInformationFormData): void
  (e: 'save'): void
}>()

const translating = ref(false)
const translationMessage = ref('')
const translationError = ref(false)

const translateFromFrench = async () => {
  const french = props.formData.description.fr.trim()
  if (!french || translating.value) return
  const hasExistingTranslations = ['en', 'es', 'it'].some(
    language => props.formData.description[language as 'en' | 'es' | 'it'].trim(),
  )
  if (hasExistingTranslations && !window.confirm('Remplacer les traductions anglaise, espagnole et italienne actuelles ?')) return

  translating.value = true
  translationMessage.value = ''
  translationError.value = false
  try {
    const translations = await translateVendeurDescriptionFromFrench(french)
    const description = {
      ...props.formData.description,
      en: translations.en || props.formData.description.en,
      es: translations.es || props.formData.description.es,
      it: translations.it || props.formData.description.it,
    }
    const updatedFormData = {
      ...props.formData,
      description,
      baseline: { ...props.formData.baseline, en: description.en, es: description.es, it: description.it },
    }
    emit('update:formData', updatedFormData)
    translationMessage.value = 'Traductions générées. Vérifie-les puis clique sur Sauvegarder.'
  } catch (value) {
    translationError.value = true
    translationMessage.value = value instanceof Error ? value.message : 'Impossible de traduire la description.'
  } finally {
    translating.value = false
  }
}

const updateFormData = (field: string, value: any) => {
  const updatedFormData = { ...props.formData }
  const keys = field.split('.')
  let current: any = updatedFormData
  
  for (let i = 0; i < keys.length - 1; i++) {
    current = current[keys[i]]
  }
  
  current[keys[keys.length - 1]] = value

  // Synchroniser baseline avec description automatiquement
  if (keys[0] === 'description' && keys.length === 2) {
    const lang = keys[1]
    if (updatedFormData.baseline) {
      updatedFormData.baseline = { ...updatedFormData.baseline, [lang]: value }
    }
  }

  emit('update:formData', updatedFormData)
}
</script>
